/**
 * Translations for the TamerinTECH landing page
 *
 * English and German copy for the homepage, header, footer and chat widget.
 * The long legal texts and the content-testing subpage live in their own
 * modules (./legal.ts, ./contentTesting.ts) and are merged in here.
 *
 * Positioning: ContextFriend is the main product and owns the page. MBot is the
 * live product. Everything else is "more from TamerinTECH", with an honest
 * status, plus an open door for technology cooperation with schools.
 */

import type { ProductId } from '../products';
import { legalTranslations } from './legal';
import type { LegalTranslations } from './legal';
import { contentTestingTranslations } from './contentTesting';
import type { ContentTestingTranslations } from './contentTesting';

/** Translated copy for one product; language-independent facts live in src/products.ts. */
export interface ProductCopy {
  name: string;
  /** Short line under the name (feature rows only). */
  tagline?: string;
  /** Translated status label; the canonical status kind lives in the registry. */
  status: string;
  text: string;
  linkText?: string;
  institutions?: { name: string; logo: string }[];
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

/** One fact about the founder, optionally with sources rendered as small links. */
export interface AboutPoint {
  text: string;
  links?: { label: string; href: string }[];
}

export interface Translations {
  language: {
    en: string;
    de: string;
  };
  slogan: string;
  googlePlayAlt: string;

  header: {
    nav: {
      contextfriend: string;
      work: string;
      about: string;
      faq: string;
      contact: string;
    };
    home: string;
    menuOpen: string;
    menuClose: string;
    switchLanguage: string;
    skip: string;
    navLabel: string;
  };

  notFound: {
    title: string;
    body: string;
    home: string;
  };

  hero: {
    headlineA: string;
    headlineB: string;
    lead: string;
    mission: string;
    cta: string;
  };

  video: {
    playAria: string;
    caption: string;
    disclosure: string;
    iframeTitle: string;
  };

  contextfriend: {
    status: string;
    recruitment: string;
    statement: string;
    body: string;
    facts: string[];
    cta: string;
    exampleQuestion: string;
    imageAlt: string;
  };

  work: {
    heading: string;
    intro: string;
    mbot: {
      features: string[];
      cta: string;
      imageAlt: string;
      viewExample: string;
      exampleLabel: string;
      exampleQuestion: string;
      exampleAnswer: string;
    };
  };

  products: {
    items: Record<ProductId, ProductCopy>;
  };

  about: {
    founderName: string;
    founderTitle: string;
    photoAlt: string;
    linkedinLabel: string;
    heading: string;
    body: string;
    teaching: string;
    points: AboutPoint[];
  };

  certification: {
    title: string;
    description: string;
    sealAlt2023: string;
    sealAlt2025: string;
  };

  faq: {
    heading: string;
    items: FaqItem[];
  };

  contact: {
    heading: string;
    body: string;
    cta: string;
    pilotCta: string;
    note: string;
  };

  footer: {
    columns: {
      products: string;
      company: string;
      legal: string;
    };
    links: {
      about: string;
      faq: string;
      contact: string;
    };
    legal: {
      cookies: string;
      impressum: string;
      privacy: string;
    };
    copyright: string;
    privacyNote: string;
  };

  chat: {
    title: string;
    online: string;
    open: string;
    close: string;
    greeting: string;
    quickReplies: string[];
    placeholder: string;
    clear: string;
    notice: string;
    privacyLink: string;
    noticeEnd: string;
    error: string;
    empty: string;
    ctaLabel: string;
    ctaBook: string;
    ctaPilot: string;
    ctaEmail: string;
  };

  legal: LegalTranslations;
  contentTesting: ContentTestingTranslations;
}

export const translations: Record<'en' | 'de', Translations> = {
  en: {
    language: {
      en: 'English',
      de: 'Deutsch'
    },

    slogan: 'Machine Learns, You Grow',

    googlePlayAlt: 'Get MBot on Google Play',

    header: {
      nav: {
        contextfriend: 'ContextFriend',
        work: 'Our work',
        about: 'About us',
        faq: 'FAQ',
        contact: 'Let’s talk'
      },
      home: 'TamerinTECH home',
      menuOpen: 'Open navigation',
      menuClose: 'Close navigation',
      switchLanguage: 'Switch to German',
      skip: 'Skip to main content',
      navLabel: 'Main navigation'
    },

    notFound: {
      title: 'Page not found',
      body: 'This address is not a page on the TamerinTECH website.',
      home: 'Back to the homepage'
    },

    hero: {
      headlineA: 'Machine Learns,',
      headlineB: 'You Grow.',
      lead: 'Technology for learning, curiosity and growth.',
      mission: 'We develop AI tools, teach young people and work with schools and researchers on new ideas in education. Our aim is to help people understand more, think for themselves and feel inspired to explore.',
      cta: 'Meet ContextFriend'
    },

    video: {
      playAria: 'Play the SAT.1 report on our AI teaching initiative at Realschule Miesbach',
      caption: 'SAT.1 visits our AI class at Realschule Miesbach · German',
      disclosure: 'YouTube loads only when you press play.',
      iframeTitle: 'SAT.1 report: TamerinTECH AI teaching initiative at Realschule Miesbach'
    },

    contextfriend: {
      status: 'School pilots',
      recruitment: 'Supervised school pilots.',
      statement: 'Revisit lessons using your class’s explanations.',
      body: 'A small classroom device connects spoken explanations with teaching materials, so students can catch up or ask follow-up questions.',
      facts: [
        'A small device in the classroom, no camera',
        'Runs on the school’s own machine, nothing goes to the cloud',
        'Teachers review, correct and delete the class memory'
      ],
      cta: 'Explore ContextFriend',
      exampleQuestion: '“How did we solve this in class?”',
      imageAlt: 'ContextFriend classroom device: a small black cylinder with a microphone array on top, standing on a desk in front of a chalkboard that reads "The lesson ends. Understanding continues."'
    },

    work: {
      heading: 'Our work in education',
      intro: 'Our projects span mathematics, learning support, museums and university teaching. We develop them with educators and research partners, from early experiments to working products.',
      mbot: {
        features: [
          'Practice, Check and Solve modes',
          'Web app and Android app, English and German',
          'Hosted on our own servers in the EU'
        ],
        cta: 'Try MBot',
        imageAlt: 'Students working with MBot on tablets during a math lesson',
        viewExample: 'View full conversation',
        exampleLabel: 'An example from MBot',
        exampleQuestion: 'Help me solve 2(x − 5) = 4(x − 7) − (x − 9)',
        exampleAnswer: 'Hint: Start by expanding both sides to remove the parentheses. Write out each term and then collect like terms.'
      }
    },

    products: {
      items: {
        contextfriend: {
          name: 'ContextFriend',
          status: 'School pilots',
          text: 'A private classroom memory that runs on the school’s own server. Pilot schools welcome.',
          linkText: 'Explore ContextFriend'
        },
        mbot: {
          name: 'MBot',
          tagline: 'Our AI math tutor for grades 5 and up.',
          status: 'Live',
          text: 'Photograph a math problem for hints and step-by-step explanations. Developed with teachers in Bavaria.',
          linkText: 'Try MBot'
        },
        londi: {
          name: 'LONDI',
          status: 'Research with LMU Munich',
          text: 'Decision support for difficulties in reading, spelling and math, for teachers, school psychologists and therapists.',
          linkText: 'Visit LONDI',
          institutions: [{ name: 'LMU Munich', logo: '/logos/lmu-logo.png' }]
        },
        tima: {
          name: 'TIMA',
          status: 'Concluded in 2024 · Offline',
          text: 'Our AI guide at Deutsches Museum Bonn adapted tours to visitors’ interests.',
          institutions: [{ name: 'Deutsches Museum Bonn', logo: '/logos/deutsches-museum-logo.svg' }]
        },
        sidai: {
          name: 'SidAI',
          status: 'Pilot with MIT and Purdue, concluded April 2025',
          text: 'Collaborative AI for project-based STEM learning, co-developed with Aalborg University.',
          institutions: [
            { name: 'MIT', logo: '/logos/mit-logo.png' },
            { name: 'Purdue University', logo: '/logos/purdue-logo.png' }
          ]
        }
      }
    },

    about: {
      founderName: 'Ron Tamerin',
      founderTitle: 'Founder, TamerinTECH',
      photoAlt: 'Ron Tamerin, founder of TamerinTECH',
      linkedinLabel: 'LinkedIn',
      heading: 'Education shapes our work.',
      body: 'At TamerinTECH, we bring software development, applied AI research and classroom teaching together. Working with educators helps us understand where technology can support learning and where people need to stay in charge.',
      teaching: 'Our AI classes and learning days give young people space to experiment: asking better questions, checking answers, protecting personal data and creating with AI.',
      points: [
        {
          text: 'AI elective “KI im Alltag” at Realschule Miesbach, taught by our founder since 2025.',
          links: [
            {
              label: 'Münchner Merkur',
              href: 'https://www.merkur.de/lokales/region-miesbach/miesbach-ort29062/it-revolution-als-nebenfach-vater-macht-ehrenamtlich-realschueler-fit-93845346.html'
            }
          ]
        },
        {
          text: 'Finalist, Munich Innovation Prize 2025, with SimpleMUC, built with Simpler Story.',
          links: [
            {
              label: 'City of Munich',
              href: 'https://stadt.muenchen.de/news/innovationspreis-muenchen2025.html'
            }
          ]
        },
        {
          text: 'Founder Ron Tamerin: 25+ years in software development and cloud architecture; AWS Certified Solutions Architect – Professional.'
        }
      ]
    },

    certification: {
      title: 'BSFZ research recognition',
      description: 'TamerinTECH’s research and development work was recognised by the BSFZ in 2023 and 2025.',
      sealAlt2023: 'BSFZ research and development seal 2023',
      sealAlt2025: 'BSFZ research and development seal 2025'
    },

    faq: {
      heading: 'Practical questions',
      items: [
        {
          id: 'contextfriend-privacy',
          question: 'How does ContextFriend protect student data?',
          answer: 'Processing stays in the school, with no cloud. The device has no camera. Voices are never stored as audio files, names and private remarks are filtered out before anything is saved, speakers stay anonymous, and students and teachers never see a transcript. The school sets how long learning content is kept, by default until the end of the school year. Teachers can review, correct and delete notes.'
        },
        {
          id: 'contextfriend-hardware',
          question: 'What does a school need to run ContextFriend?',
          answer: 'A classroom device, a server in the school and the school network. Lessons work without internet. From home, students reach it only through the school’s secure access, if the school switches it on.'
        },
        {
          id: 'contextfriend-pilot',
          question: 'What does the ContextFriend pilot include?',
          answer: 'One classroom device, a server in the school, installation, access for the class and support. Apply using the pilot link: three questions and your email address. Schools start with a supervised pilot; there are no public prices.'
        },
        {
          id: 'what-is-mbot',
          question: 'Where can students use MBot?',
          answer: 'MBot is available on the web and as an Android app, in English and German, for students in grade 5 and up. It runs on our own servers in the EU.'
        },
        {
          id: 'cooperation',
          question: 'Can we work together on an education project?',
          answer: 'Yes. We work with schools, universities and education partners on joint pilots, applied research and AI prototypes for teaching. Book a call to discuss your idea.'
        }
      ]
    },

    contact: {
      heading: 'Bring ContextFriend\nto your classroom.',
      body: 'Discuss a pilot, MBot, or a project for your school.',
      cta: 'Book a conversation',
      pilotCta: 'Apply for a pilot',
      note: '30 minutes, in English or German.'
    },

    footer: {
      columns: {
        products: 'Products',
        company: 'Company',
        legal: 'Legal'
      },
      links: {
        about: 'About us',
        faq: 'FAQ',
        contact: 'Contact'
      },
      legal: {
        cookies: 'Cookie Policy',
        impressum: 'Impressum',
        privacy: 'Privacy Policy'
      },
      copyright: '© {year} TamerinTECH GmbH. All rights reserved.',
      privacyNote: 'See our Privacy Policy for cookies, analytics and advertising.'
    },

    chat: {
      title: 'TamerinTECH Chat',
      online: 'Online',
      open: 'Chat with us',
      close: 'Close chat',
      greeting: 'Hi, I’m the TamerinTECH assistant. Ask me about ContextFriend, MBot or working with us. I can also pass your details on to our team.',
      quickReplies: [
        'What is ContextFriend?',
        'I’d like a ContextFriend pilot at my school',
        'I’m a parent looking for math support',
        'I have an idea for a cooperation'
      ],
      placeholder: 'Type a message...',
      clear: 'Clear',
      notice: 'AI can make mistakes. Please verify important information. By using this chat, you agree to our',
      privacyLink: 'privacy policy',
      noticeEnd: '.',
      error: 'Sorry, there was an error. Please try again or write to info@tamerin.tech.',
      empty: 'Sorry, I could not process that.',
      ctaLabel: 'Next steps',
      ctaBook: 'Book a call',
      ctaPilot: 'Apply for a pilot',
      ctaEmail: 'Email us'
    },

    legal: legalTranslations.en,
    contentTesting: contentTestingTranslations.en
  },

  de: {
    language: {
      en: 'English',
      de: 'Deutsch'
    },

    slogan: 'Machine Learns, You Grow',

    googlePlayAlt: 'MBot bei Google Play laden',

    header: {
      nav: {
        contextfriend: 'ContextFriend',
        work: 'Unsere Projekte',
        about: 'Über uns',
        faq: 'FAQ',
        contact: 'Kontakt'
      },
      home: 'TamerinTECH Startseite',
      menuOpen: 'Navigation öffnen',
      menuClose: 'Navigation schließen',
      switchLanguage: 'Switch to English',
      skip: 'Zum Inhalt springen',
      navLabel: 'Hauptnavigation'
    },

    notFound: {
      title: 'Seite nicht gefunden',
      body: 'Diese Adresse ist keine Seite der TamerinTECH-Website.',
      home: 'Zur Startseite'
    },

    hero: {
      headlineA: 'Machine Learns,',
      headlineB: 'You Grow.',
      lead: 'Technologie für Lernen, Neugier und persönliche Entwicklung.',
      mission: 'Wir entwickeln KI-Werkzeuge, unterrichten junge Menschen und arbeiten mit Schulen und Forschungspartnern an neuen Ideen für die Bildung. Wir möchten Menschen helfen, mehr zu verstehen, selbstständig zu denken und mit Freude Neues zu entdecken.',
      cta: 'ContextFriend entdecken'
    },

    video: {
      playAria: 'SAT.1-Beitrag über unsere KI-Unterrichtsinitiative an der Realschule Miesbach abspielen',
      caption: 'SAT.1 zu Besuch in unserem KI-Unterricht an der Realschule Miesbach · Deutsch',
      disclosure: 'YouTube wird erst beim Abspielen geladen.',
      iframeTitle: 'SAT.1-Beitrag: KI-Unterrichtsinitiative von TamerinTECH an der Realschule Miesbach'
    },

    contextfriend: {
      status: 'Schulpiloten',
      recruitment: 'Begleitete Schulpiloten.',
      statement: 'Unterricht wiederholen – mit den Erklärungen der eigenen Klasse.',
      body: 'Ein kleines Gerät verbindet die Erklärungen aus dem Unterricht mit den Lehrmaterialien. So können Schülerinnen und Schüler Versäumtes nachholen und nachfragen.',
      facts: [
        'Ein kleines Gerät im Klassenzimmer, ohne Kamera',
        'Läuft auf dem Rechner der Schule, nichts geht in die Cloud',
        'Lehrkräfte prüfen, korrigieren und löschen das Klassengedächtnis'
      ],
      cta: 'ContextFriend kennenlernen',
      exampleQuestion: '„Wie haben wir das im Unterricht gelöst?“',
      imageAlt: 'ContextFriend-Gerät für das Klassenzimmer: ein kleiner schwarzer Zylinder mit Mikrofon-Array auf einem Pult vor einer Tafel mit der Aufschrift „Die Stunde endet. Das Verstehen geht weiter.“'
    },

    work: {
      heading: 'Unsere Arbeit in der Bildung',
      intro: 'Unsere Projekte reichen vom Mathelernen und der Lernförderung über Museumsbesuche bis zur Hochschullehre. Gemeinsam mit Lehrkräften und Forschungspartnern entwickeln wir erste Ideen zu nutzbaren Anwendungen.',
      mbot: {
        features: [
          'Üben-, Prüfen- und Lösen-Modus',
          'Web-App und Android-App, Deutsch und Englisch',
          'Betrieb auf eigenen Servern in der EU'
        ],
        cta: 'MBot ausprobieren',
        imageAlt: 'Schülerinnen und Schüler arbeiten im Matheunterricht mit MBot auf Tablets',
        viewExample: 'Vollständiges Gespräch ansehen',
        exampleLabel: 'Ein Beispiel aus MBot',
        exampleQuestion: 'Hilf mir, 2(x − 5) = 4(x − 7) − (x − 9) zu lösen.',
        exampleAnswer: 'Tipp: Löse zuerst auf beiden Seiten die Klammern auf. Schreibe jeden Term aus und fasse dann gleichartige Terme zusammen.'
      }
    },

    products: {
      items: {
        contextfriend: {
          name: 'ContextFriend',
          status: 'Schulpiloten',
          text: 'Ein privates Klassengedächtnis, das auf dem Server der Schule läuft. Pilotschulen willkommen.',
          linkText: 'ContextFriend kennenlernen'
        },
        mbot: {
          name: 'MBot',
          tagline: 'Unser KI-Mathe-Tutor ab Klasse 5.',
          status: 'Live',
          text: 'Eine Matheaufgabe fotografieren und Hinweise sowie Erklärungen Schritt für Schritt erhalten. Mit Lehrkräften in Bayern entwickelt.',
          linkText: 'MBot ausprobieren'
        },
        londi: {
          name: 'LONDI',
          status: 'Forschung mit der LMU München',
          text: 'Entscheidungshilfe bei Schwierigkeiten im Lesen, Rechtschreiben und Rechnen, für Lehrkräfte, Schulpsychologie und Therapie.',
          linkText: 'LONDI besuchen',
          institutions: [{ name: 'LMU München', logo: '/logos/lmu-logo.png' }]
        },
        tima: {
          name: 'TIMA',
          status: '2024 abgeschlossen · Offline',
          text: 'Unser KI-Guide im Deutschen Museum Bonn passte Touren an die Interessen der Besucherinnen und Besucher an.',
          institutions: [{ name: 'Deutsches Museum Bonn', logo: '/logos/deutsches-museum-logo.svg' }]
        },
        sidai: {
          name: 'SidAI',
          status: 'Pilot mit MIT und Purdue, abgeschlossen April 2025',
          text: 'Kollaborative KI für projektbasiertes Lernen in MINT-Fächern, gemeinsam mit der Aalborg University entwickelt.',
          institutions: [
            { name: 'MIT', logo: '/logos/mit-logo.png' },
            { name: 'Purdue University', logo: '/logos/purdue-logo.png' }
          ]
        }
      }
    },

    about: {
      founderName: 'Ron Tamerin',
      founderTitle: 'Gründer, TamerinTECH',
      photoAlt: 'Ron Tamerin, Gründer von TamerinTECH',
      linkedinLabel: 'LinkedIn',
      heading: 'Bildung prägt unsere Arbeit.',
      body: 'Bei TamerinTECH verbinden wir Softwareentwicklung, angewandte KI-Forschung und Unterrichtspraxis. Die Zusammenarbeit mit Lehrkräften zeigt uns, wo Technologie beim Lernen helfen kann und wo Menschen die Entscheidungen treffen müssen.',
      teaching: 'Unser KI-Unterricht und unsere Kompetenztage geben jungen Menschen Raum zum Ausprobieren: bessere Fragen stellen, Antworten prüfen, persönliche Daten schützen und selbst mit KI gestalten.',
      points: [
        {
          text: 'Wahlfach „KI im Alltag“ an der Realschule Miesbach, seit 2025 von unserem Gründer unterrichtet.',
          links: [
            {
              label: 'Münchner Merkur',
              href: 'https://www.merkur.de/lokales/region-miesbach/miesbach-ort29062/it-revolution-als-nebenfach-vater-macht-ehrenamtlich-realschueler-fit-93845346.html'
            }
          ]
        },
        {
          text: 'Finalist beim Münchner Innovationspreis 2025 mit SimpleMUC, entwickelt mit Simpler Story.',
          links: [
            {
              label: 'Stadt München',
              href: 'https://stadt.muenchen.de/news/innovationspreis-muenchen2025.html'
            }
          ]
        },
        {
          text: 'Gründer Ron Tamerin: über 25 Jahre Softwareentwicklung und Cloud-Architektur; AWS Certified Solutions Architect – Professional.'
        }
      ]
    },

    certification: {
      title: 'BSFZ-Forschungsbescheinigung',
      description: 'Die Forschungs- und Entwicklungsarbeit von TamerinTECH wurde 2023 und 2025 von der BSFZ bescheinigt.',
      sealAlt2023: 'BSFZ-Siegel für Forschung und Entwicklung 2023',
      sealAlt2025: 'BSFZ-Siegel für Forschung und Entwicklung 2025'
    },

    faq: {
      heading: 'Häufige Fragen',
      items: [
        {
          id: 'contextfriend-privacy',
          question: 'Wie schützt ContextFriend die Daten der Lernenden?',
          answer: 'Die Verarbeitung bleibt in der Schule, ohne Cloud. Das Gerät hat keine Kamera. Stimmen werden nie als Audiodatei gespeichert, Namen und private Bemerkungen werden herausgefiltert, bevor etwas gespeichert wird, wer spricht bleibt anonym und Lernende und Lehrkräfte sehen nie ein Transkript. Die Schule legt fest, wie lange Lerninhalte gespeichert werden, standardmäßig bis zum Ende des Schuljahres. Lehrkräfte können Lernnotizen prüfen, korrigieren oder löschen.'
        },
        {
          id: 'contextfriend-hardware',
          question: 'Was braucht eine Schule für ContextFriend?',
          answer: 'Ein Gerät im Klassenzimmer, einen Server in der Schule und das Schulnetz. Der Unterricht läuft ohne Internet. Von zu Hause greifen Lernende nur über den gesicherten Zugang der Schule zu, und nur wenn die Schule das freischaltet.'
        },
        {
          id: 'contextfriend-pilot',
          question: 'Was umfasst der ContextFriend-Pilot?',
          answer: 'Ein Gerät im Klassenzimmer, einen Server in der Schule, die Einrichtung, den Zugang für die Klasse und Begleitung. Die Bewerbung über den Pilot-Link besteht aus drei Fragen und Ihrer E-Mail-Adresse. Schulen beginnen mit einem begleiteten Pilot; öffentliche Preise gibt es nicht.'
        },
        {
          id: 'what-is-mbot',
          question: 'Wo können Schülerinnen und Schüler MBot nutzen?',
          answer: 'MBot ist als Web-App und Android-App auf Deutsch und Englisch verfügbar, für Lernende ab Klasse 5. Der Betrieb läuft auf unseren eigenen Servern in der EU.'
        },
        {
          id: 'cooperation',
          question: 'Können wir gemeinsam ein Bildungsprojekt umsetzen?',
          answer: 'Ja. Wir arbeiten mit Schulen, Hochschulen und Bildungspartnern an gemeinsamen Piloten, angewandter Forschung und KI-Prototypen für den Unterricht. Vereinbaren Sie ein Gespräch über Ihre Idee.'
        }
      ]
    },

    contact: {
      heading: 'ContextFriend für\nIhre Klasse.',
      body: 'Sprechen wir über einen Piloten, MBot oder ein Projekt für Ihre Schule.',
      cta: 'Gespräch vereinbaren',
      pilotCta: 'Pilot beantragen',
      note: '30 Minuten, auf Deutsch oder Englisch.'
    },

    footer: {
      columns: {
        products: 'Produkte',
        company: 'Unternehmen',
        legal: 'Rechtliches'
      },
      links: {
        about: 'Über uns',
        faq: 'FAQ',
        contact: 'Kontakt'
      },
      legal: {
        cookies: 'Cookie-Richtlinie',
        impressum: 'Impressum',
        privacy: 'Datenschutzerklärung'
      },
      copyright: '© {year} TamerinTECH GmbH. Alle Rechte vorbehalten.',
      privacyNote: 'Informationen zu Cookies, Analyse und Werbung finden Sie in der Datenschutzerklärung.'
    },

    chat: {
      title: 'TamerinTECH Chat',
      online: 'Online',
      open: 'Chat öffnen',
      close: 'Chat schließen',
      greeting: 'Hallo, ich bin der TamerinTECH-Assistent. Fragen Sie mich zu ContextFriend, MBot oder einer Zusammenarbeit. Ich kann Ihre Kontaktdaten auch an unser Team weitergeben.',
      quickReplies: [
        'Was ist ContextFriend?',
        'Ich möchte einen ContextFriend-Pilot an meiner Schule',
        'Ich bin Elternteil und suche Mathe-Unterstützung',
        'Ich habe eine Idee für eine Kooperation'
      ],
      placeholder: 'Nachricht eingeben...',
      clear: 'Löschen',
      notice: 'Diese KI kann Fehler machen. Bitte überprüfen Sie wichtige Informationen. Durch die Nutzung stimmen Sie unserer',
      privacyLink: 'Datenschutzerklärung',
      noticeEnd: ' zu.',
      error: 'Entschuldigung, es gab einen Fehler. Bitte versuchen Sie es erneut oder schreiben Sie an info@tamerin.tech.',
      empty: 'Entschuldigung, ich konnte das nicht verarbeiten.',
      ctaLabel: 'Nächste Schritte',
      ctaBook: 'Gespräch buchen',
      ctaPilot: 'Pilot beantragen',
      ctaEmail: 'E-Mail schreiben'
    },

    legal: legalTranslations.de,
    contentTesting: contentTestingTranslations.de
  }
};
